import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/auth/auth-store';
import { useAppointmentCancellationReasons } from '@/scheduling/services/use-appointment-cancellation-reasons';
import { useCancelAppointment } from '@/scheduling/services/use-cancel-appointment';
import { useCancelRecurringAppointment } from '@/scheduling/services/use-cancel-recurring-appointment';
import type {
  AppointmentCancellationReason,
  AppointmentsDetailsResponse,
} from '@/types/scheduling-api';
import { cn } from '@/lib/utils';
import { cancellationNoteBody } from '@/scheduling/lib/cancellation-note';
import { CancellationNoteField } from './cancellation-note-field';
import { APPOINTMENT_LIST_QUERIES } from '@/scheduling/services/appointment-list-keys';

/**
 * CancelAppointmentDialog — confirms an appointment cancellation.
 *
 * Recurring appointments get a two-step flow that mirrors Flutter
 * appointment_status.dart:860-940 (recurring popup) → calendar_tab.dart:1700:
 *   Step 1 — scope: This only · This and following · All in series
 *   Step 2 — reason picker
 *
 * Dispatch (Flutter parity):
 *   - this-only           → useCancelAppointment (regardless of isRecurring)
 *   - following           → useCancelRecurringAppointment with appointmentDate
 *   - all                 → useCancelRecurringAppointment with allAppointment=true
 *   - non-recurring       → useCancelAppointment (this-only path)
 *
 * The optional Note is sent only on the single-cancel path — `CancelRecurring`
 * does not accept it, so the field is hidden for the following / all scopes.
 *
 * Bodies include staffId (the logged-in user) and practitionerId (the
 * doctor on the appointment), matching Flutter exactly.
 */

export type RecurringScope = 'this-only' | 'following' | 'all';

interface CancelTarget {
  appointmentId: string;
  patientId?: string;
  /** Provider id from the participant list. Sent as `practitionerId` in the
   *  Flutter cancel body and required server-side for audit. */
  practitionerId?: string;
  /** ISO start datetime — required when recurring scope is "following" so
   *  the backend knows from which occurrence onward to cancel. */
  startISO?: string;
  /** Recurring id when the appointment is part of a series. Empty/undefined
   *  means non-recurring; the dialog skips the scope picker. */
  recurringId?: string;
  /** Patient's first name — for the Flutter notify message ("we will notify
   *  {firstName}…"). Falls back to "the patient" when absent. */
  patientFirstName?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: CancelTarget | null;
  /**
   * Pre-select the recurring scope when the dialog opens (PROD-1019). The
   * series-tab row menu has three explicit Cancel items (this / following /
   * all) that each open the dialog at the matching scope. Ignored for
   * non-recurring targets (scope is locked to `this-only`). Omit to keep the
   * default behaviour (recurring → user picks scope).
   */
  initialScope?: RecurringScope;
}

export function CancelAppointmentDialog({ open, onOpenChange, target, initialScope }: Props) {
  const queryClient = useQueryClient();
  const isRecurring = Boolean(target?.recurringId);

  const [scope, setScope] = useState<RecurringScope | null>(null);
  const [reasonCode, setReasonCode] = useState<string>('');
  const [note, setNote] = useState('');

  const { data: reasons = [], isLoading: reasonsLoading } =
    useAppointmentCancellationReasons();
  const cancelMutation = useCancelAppointment();
  const cancelRecurringMutation = useCancelRecurringAppointment();

  // Reset state each time the dialog opens. Non-recurring → scope locked
  // to this-only so the reason picker shows immediately.
  useEffect(() => {
    if (open) {
      setScope(isRecurring ? (initialScope ?? null) : 'this-only');
      setReasonCode('');
      setNote('');
    }
  }, [open, isRecurring, initialScope]);

  if (!target) return null;

  const reasonObj: AppointmentCancellationReason | undefined = reasons.find(
    (r) => r.code === reasonCode,
  );

  const canSubmit =
    Boolean(reasonObj) &&
    scope !== null &&
    !cancelMutation.isPending &&
    !cancelRecurringMutation.isPending;

  // Per-scope title — verbatim from Flutter `getCancelAlertTitle`
  // (scrips_core/utils/utils.dart:676). Neutral prompt while a recurring
  // scope hasn't been picked yet.
  const dialogTitle =
    scope === 'following'
      ? 'Do you really want to cancel this and following appointments?'
      : scope === 'all'
        ? 'Do you really want to cancel all appointments in the series?'
        : scope === 'this-only'
          ? isRecurring
            ? 'Do you really want to cancel only this appointment?'
            : 'Do you really want to cancel this appointment?'
          : 'Cancel appointment';

  // Notify message — verbatim from Flutter (calendar_tab.dart:1618).
  const notifyName = target.patientFirstName?.trim() || 'the patient';

  const submit = async () => {
    if (!reasonObj || !scope) return;
    const staffId = useAuthStore.getState().user?.userId;

    if (scope === 'this-only' || !target.recurringId) {
      // Single-occurrence cancel — same endpoint regardless of whether the
      // appointment is part of a series. Flutter cancellationType 0 or 1.
      await cancelMutation.mutateAsync({
        appointmentId: target.appointmentId,
        cancellationReason: reasonObj,
        patientId: target.patientId,
        practitionerId: target.practitionerId,
        staffId,
        ...cancellationNoteBody(note),
      });
    } else {
      // Recurring cancel — types 2 (following) or 3 (all in series).
      await cancelRecurringMutation.mutateAsync({
        recurringId: target.recurringId,
        cancellationReason: reasonObj,
        patientId: target.patientId,
        practitionerId: target.practitionerId,
        staffId,
        allAppointment: scope === 'all',
        appointmentDate: scope === 'following' ? target.startISO : undefined,
      });
    }

    // Optimistic: drop this row from every cached appointments query.
    // For "following" / "all" we only know enough to drop this id;
    // SignalR delivers the rest of the series shortly.
    queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
      APPOINTMENT_LIST_QUERIES,
      (prev) => {
        if (!prev?.appointments) return prev;
        const next = prev.appointments.filter(
          (a) => a.id !== target.appointmentId,
        );
        if (next.length === prev.appointments.length) return prev;
        return { ...prev, appointments: next };
      },
    );
    void queryClient.invalidateQueries(APPOINTMENT_LIST_QUERIES);

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[var(--dialog-width-form)]">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
        </DialogHeader>

        <p className="[font:var(--type-body)] text-[var(--ink-meta)]">
          When you cancel we will notify {notifyName} about this change using
          their preferred communication method.
        </p>

        <div className="flex flex-col gap-4 py-2">
          {isRecurring && (
            <ScopePicker scope={scope} onChange={setScope} />
          )}

          {(scope || !isRecurring) && (
            <ReasonPicker
              reasons={reasons}
              loading={reasonsLoading}
              value={reasonCode}
              onChange={setReasonCode}
            />
          )}

          {scope === 'this-only' && (
            <CancellationNoteField
              value={note}
              onChange={setNote}
              disabled={cancelMutation.isPending}
            />
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={cancelMutation.isPending || cancelRecurringMutation.isPending}
          >
            Keep appointment
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canSubmit}
          >
            {cancelMutation.isPending || cancelRecurringMutation.isPending
              ? 'Cancelling…'
              : 'Cancel appointment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

function ScopePicker({
  scope,
  onChange,
}: {
  scope: RecurringScope | null;
  onChange: (s: RecurringScope) => void;
}) {
  const options: { value: RecurringScope; label: string; description: string }[] = [
    {
      value: 'this-only',
      label: 'Cancel only this appointment',
      description: 'Cancel just this occurrence',
    },
    {
      value: 'following',
      label: 'Cancel this and following appointments',
      description: 'Cancel this occurrence and every future one in the series',
    },
    {
      value: 'all',
      label: 'Cancel all appointments in the series',
      description: 'Cancel every occurrence — past and future',
    },
  ];

  return (
    <div>
      <div className="text-sm font-semibold text-text-primary mb-2">
        This is a recurring appointment
      </div>
      <div className="flex flex-col gap-2">
        {options.map((opt) => {
          const active = scope === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                'text-left rounded-card border p-3 transition-colors',
                active
                  ? 'border-brand-primary bg-[var(--accent-active-bg)]'
                  : 'border-[var(--surface-line)] hover:bg-grey-100',
              )}
            >
              <div
                className={cn(
                  'text-sm font-medium',
                  active ? 'text-brand-primary' : 'text-text-primary',
                )}
              >
                {opt.label}
              </div>
              <div className="text-xs text-text-muted mt-1">
                {opt.description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReasonPicker({
  reasons,
  loading,
  value,
  onChange,
}: {
  reasons: AppointmentCancellationReason[];
  loading: boolean;
  value: string;
  onChange: (code: string) => void;
}) {
  return (
    <div>
      <label
        htmlFor="cancel-reason"
        className="text-sm font-semibold text-text-primary block mb-2"
      >
        Cancellation reason
      </label>
      <select
        id="cancel-reason"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className="w-full h-10 px-3 text-sm rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
      >
        <option value="">
          {loading ? 'Loading reasons…' : 'Select a reason'}
        </option>
        {reasons.map((r) => (
          <option key={r.code} value={r.code}>
            {r.display}
          </option>
        ))}
      </select>
      {!loading && reasons.length === 0 && (
        <p className="text-xs text-text-hint mt-1">
          No cancellation reasons configured.
        </p>
      )}
    </div>
  );
}
